import { useTheme } from '@mui/material'
import { FillPaint, LinePaint } from 'mapbox-gl'
import { FC } from 'react'
import { Layer, Source } from 'react-map-gl/dist/es5'
import { MapTheme } from '../../..'
import { Config } from '../../../types'
import { SelectableLayer } from './SelectableLayer'
import { GEOPOLITICAL } from './const'

export const GEOPOLITICAL_STATE_HOVER_LAYER = 'state-fill'
export const GEOPOLITICAL_STATE_OUTLINE_LAYER = 'state-outline'
export const GEOPOLITICAL_COUNTY_HOVER_LAYER = 'county-fill'
export const GEOPOLITICAL_COUNTY_OUTLINE = 'county-outline'
export const GEOPOLITICAL_TOWNSHIP_HOVER_LAYER = 'township-fill'
export const GEOPOLITICAL_TOWNSHIP_OUTLINE = 'township-outline'

interface GeopoliticalLayerProps {
  counties?: boolean
  countyFillPaint?: FillPaint
  countyLinePaint?: LinePaint
  minZoom?: number
  states?: boolean
  stateFillPaint?: FillPaint
  stateLinePaint?: LinePaint
  townships?: boolean
  townshipFillPaint?: FillPaint
  townshipLinePaint?: LinePaint
  hideSelectedGeometries?: boolean
}

export const GeopoliticalLayer: FC<GeopoliticalLayerProps> = ({
  children,
  counties = true,
  countyFillPaint = {},
  countyLinePaint = {},
  minZoom = 3,
  states,
  stateFillPaint,
  stateLinePaint,
  townships = true,
  townshipFillPaint = {},
  townshipLinePaint = {},
  hideSelectedGeometries = false,
}) => {
  const theme = useTheme<MapTheme>()
  const maptilesServiceUrl = Config.get('MAPTILES_SERVICE_URL')

  return (
    <Source
      id={GEOPOLITICAL.SOURCE}
      type="vector"
      tiles={[`${maptilesServiceUrl}geopolitical/{z}/{x}/{y}.pbf`]}
      minzoom={minZoom}
      maxzoom={28}
    >
      <Layer
        id={GEOPOLITICAL_STATE_OUTLINE_LAYER}
        type="line"
        beforeId="data-layer-anchor"
        source-layer={GEOPOLITICAL.STATES.SHAPES}
        layout={{
          visibility: states ? 'visible' : 'none',
        }}
        paint={{
          'line-color': theme.palette.stateLine,
          'line-width': ['interpolate', ['exponential', 1.51], ['zoom'], 0, 0.5, 5.5, 2],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 5.5, 1, 5.75, 0.25, 7, 0],
          ...stateLinePaint,
        }}
      />
      <SelectableLayer
        id={GEOPOLITICAL_STATE_HOVER_LAYER}
        source-layer={GEOPOLITICAL.STATES.SHAPES}
        layout={{
          visibility: states ? 'visible' : 'none',
        }}
        hideSelectedGeometries={hideSelectedGeometries}
      />
      <Layer
        id={GEOPOLITICAL_COUNTY_HOVER_LAYER}
        type="fill"
        beforeId="data-layer-anchor"
        source-layer={GEOPOLITICAL.COUNTIES.SHAPES}
        layout={{
          visibility: counties ? 'visible' : 'none',
        }}
        paint={{
          'fill-color': 'transparent',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            minZoom,
            0,
            minZoom + 0.25,
            1,
            8.5,
            1,
            8.75,
            1,
            10,
            0,
          ],
          ...countyFillPaint,
        }}
      />
      <Layer
        id={GEOPOLITICAL_COUNTY_OUTLINE}
        type="line"
        beforeId="data-layer-anchor"
        source-layer={GEOPOLITICAL.COUNTIES.SHAPES}
        layout={{
          visibility: counties ? 'visible' : 'none',
        }}
        paint={{
          'line-color': theme.palette.countyLine,
          'line-width': ['interpolate', ['exponential', 1.51], ['zoom'], 3, 0.5, 8.5, 2],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            minZoom,
            0,
            minZoom + 0.25,
            1,
            8.5,
            1,
            8.75,
            0.25,
            10,
            0,
          ],
          ...countyLinePaint,
        }}
      />
      <SelectableLayer
        id={GEOPOLITICAL_COUNTY_HOVER_LAYER}
        source-layer={GEOPOLITICAL.COUNTIES.SHAPES}
        layout={{
          visibility: counties ? 'visible' : 'none',
        }}
        hideSelectedGeometries={hideSelectedGeometries}
      />
      <Layer
        id={GEOPOLITICAL_TOWNSHIP_OUTLINE}
        type="line"
        beforeId="data-layer-anchor"
        source-layer={GEOPOLITICAL.TOWNSHIPS.SHAPES}
        layout={{
          visibility: townships ? 'visible' : 'none',
        }}
        paint={{
          'line-color': theme.palette.townshipLine,
          'line-width': ['interpolate', ['exponential', 1.51], ['zoom'], 8, 0.9, 16, 4],
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 8.75, 0, 9, 1, 12, 1, 13, 0],
          ...townshipLinePaint,
        }}
      />
      <SelectableLayer
        id={GEOPOLITICAL_TOWNSHIP_OUTLINE}
        source-layer={GEOPOLITICAL.TOWNSHIPS.SHAPES}
        layout={{
          visibility: townships ? 'visible' : 'none',
        }}
        hideSelectedGeometries={hideSelectedGeometries}
      />
      {/* <Layer
        id={GEOPOLITICAL_TOWNSHIP_HOVER_LAYER}
        type="fill"
        beforeId="data-layer-anchor"
        source-layer={GEOPOLITICAL.TOWNSHIPS.SHAPES}
        layout={{
          visibility: townships ? 'visible' : 'none',
        }}
        paint={{
          'fill-color': 'transparent',
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 8.75, 0, 9, 1, 12, 1, 13, 0],
          ...townshipFillPaint,
        }}
      /> */}
      {children}
      <Layer
        id="county-label"
        type="symbol"
        beforeId="state-label"
        source-layer={GEOPOLITICAL.COUNTIES.CENTERS}
        layout={{
          visibility: counties ? 'visible' : 'none',
          'text-field': ['to-string', ['get', 'label']],
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 4, 8.5, 16],
        }}
        paint={{
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6,
            0,
            6.25,
            1,
            8.5,
            1,
            8.75,
            0.25,
            10,
            0,
          ],
          'text-halo-color': theme.palette.countyTextHalo,
          'text-halo-width': 1,
          'text-halo-blur': 1,
          'text-color': theme.palette.countyText,
        }}
      />
      <Layer
        id="township-label"
        type="symbol"
        beforeId="county-label"
        source-layer={GEOPOLITICAL.TOWNSHIPS.CENTERS}
        layout={{
          visibility: townships ? 'visible' : 'none',
          'text-field': ['to-string', ['get', 'label']],
          // 'text-font': ['Nunito Sans SemiBold', 'Arial Unicode MS Regular'],
          'text-size': [
            'interpolate',
            ['cubic-bezier', 0.2, 0, 0.9, 1],
            ['zoom'],
            3,
            12,
            5,
            14,
            13,
            12,
          ],
          'text-letter-spacing': 0.05,
        }}
        paint={{
          'text-color': theme.palette.townshipTextColor,
          'text-opacity': ['interpolate', ['linear'], ['zoom'], 8.75, 0, 9, 1, 12, 1, 13, 0],
          'text-halo-color': theme.palette.townshipTextHalo,
          'text-halo-blur': 1,
          'text-halo-width': 1,
        }}
      />
    </Source>
  )
}
